import * as actions from './Actions';

// Normalize various possible API response shapes into a consistent evaluation object
const parseEvaluation = (payload) => {
  if (!payload) return null;

  // Support nested response { evaluation: {...} }
  const source = payload.evaluation || payload;

  // Try to resolve an id from multiple possible fields
  const idCandidate =
    source.evaluationId ||
    source.id ||
    source._id ||
    source.evaluation_id ||
    null;

  return {
    evaluationId: idCandidate || `temp_${Date.now()}`,
    employee_id: source.employee_id ?? source.employeeId ?? null,
    skill_id: source.skill_id ?? source.skillId ?? null,
    // Preserve nested skill object if present so UI can render skill.name
    skill: source.skill || null,
    evaluation_date: source.evaluation_date ?? source.evaluationDate ?? null,
    score: source.score ?? null,
    level: source.level ?? null,
    comments: source.comments ?? '',
    evaluation_type: source.evaluation_type ?? source.evaluationType ?? null,
    createdAt: source.createdAt || new Date().toISOString(),
    updatedAt: source.updatedAt || new Date().toISOString(),
    // Normalized evaluator fields
    evaluatedBy: source.evaluatedBy || source.createdBy || source.raterUser || source.evaluator || source.rater || null,
    evaluatorName: (() => {
      const u = source.evaluatedBy || source.createdBy || source.raterUser || source.evaluator || source.rater || null;
      return (u?.username || u?.email || source.evaluatorName || '-')
    })(),
    createdBy: source.createdBy || null,
    // Carry over evidences array when present
    evidences: Array.isArray(source.evidences) ? source.evidences : [],
  };
};

// State shape supports both array (legacy) and object { items: [], deletedIds: [] }
const initialState = { items: [], deletedIds: [] };

export const evaluationsReducer = (state = initialState, action) => {
  switch (action.type) {
    case actions.INITIALIZE_EVALUATIONS:
      if (!Array.isArray(action.payload)) return state;
      return {
        items: action.payload.map((e) => parseEvaluation(e)).filter(Boolean),
        deletedIds: Array.isArray(state?.deletedIds) ? state.deletedIds : [],
      };

    case actions.ADD_EVALUATION: {
      const parsed = parseEvaluation(action.payload);
      if (!parsed) return state;
      const items = Array.isArray(state) ? state : (state.items || []);
      const next = [...items, parsed];
      return Array.isArray(state)
        ? next
        : { ...state, items: next };
    }

    case actions.UPDATE_EVALUATION: {
      const updated = parseEvaluation(action.payload);
      if (!updated) return state;
      const items = Array.isArray(state) ? state : (state.items || []);
      const next = items.map((e) => {
        if (e.evaluationId !== updated.evaluationId) return e;
        // Preserve existing evidences if update payload didn't include them
        const evidences = Array.isArray(updated.evidences) && updated.evidences.length > 0 ? updated.evidences : (e.evidences || []);
        return { ...e, ...updated, evidences };
      });
      return Array.isArray(state)
        ? next
        : { ...state, items: next };
    }

    case actions.DELETE_EVALUATION: {
      const toDelete = parseEvaluation(action.payload);
      if (!toDelete) return state;
      const items = Array.isArray(state) ? state : (state.items || []);
      const next = items.filter((e) => e.evaluationId !== toDelete.evaluationId);
      if (Array.isArray(state)) return next;
      const prevDeleted = Array.isArray(state.deletedIds) ? state.deletedIds : [];
      // Deduplicate
      const deletedIds = Array.from(new Set([...prevDeleted, String(toDelete.evaluationId)]));
      return { ...state, items: next, deletedIds };
    }

    case actions.CLEAR_EVALUATIONS_LIST:
      return { items: [], deletedIds: [] };

    default:
      return state;
  }
};

